import { UniQChatMessage } from "us.common/interfaces";

// Session storage key for persisting messages - includes user session info
const UNIQ_MESSAGES_STORAGE_KEY = "uniq_chat_messages";

/**
 * Utility functions for managing chat message persistence in localStorage.
 * Messages will persist until the user's session expires (new access token generated):
 * - Survives browser refreshes
 * - Survives tab/browser closures
 * - Survives computer restarts
 * - Cleared when user logs out or session expires
 * - Cleared when onReset prop triggers (modal close)
 */
export const MessageStorage = {
  // Generate a session-specific storage key
  getSessionKey: (userId?: string): string => {
    return `${UNIQ_MESSAGES_STORAGE_KEY}_${userId || "anonymous"}`;
  },

  // Load messages from localStorage for current user session
  loadMessages: (userId?: string): UniQChatMessage[] => {
    try {
      const sessionKey = MessageStorage.getSessionKey(userId);
      const storedMessages = localStorage.getItem(sessionKey);
      if (storedMessages) {
        const parsedMessages = JSON.parse(storedMessages);
        // Ensure timestamp is a Date object
        return parsedMessages.map((msg: any) => ({
          ...msg,
          timestamp: new Date(msg.timestamp),
        }));
      }
      return [];
    } catch (error) {
      console.warn("Failed to load messages from localStorage:", error);
      return [];
    }
  },

  // Save messages to localStorage for current user session
  saveMessages: (messages: UniQChatMessage[], userId?: string): void => {
    try {
      const sessionKey = MessageStorage.getSessionKey(userId);
      localStorage.setItem(sessionKey, JSON.stringify(messages));
    } catch (error) {
      console.warn("Failed to save messages to localStorage:", error);
    }
  },

  // Clear messages from localStorage for current user session
  clearMessages: (userId?: string): void => {
    try {
      const sessionKey = MessageStorage.getSessionKey(userId);
      localStorage.removeItem(sessionKey);
    } catch (error) {
      console.warn("Failed to clear messages from localStorage:", error);
    }
  },

  // Clear all UniQ messages for all users (useful for logout scenarios)
  clearAllMessages: (): void => {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach((key) => {
        if (key.startsWith(UNIQ_MESSAGES_STORAGE_KEY)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn("Failed to clear all messages from localStorage:", error);
    }
  },

  // Keep chat state in sync across tabs/windows by reacting to storage events
  subscribeToStorageChanges: (
    userId: string | undefined,
    onMessagesUpdate: (messages: UniQChatMessage[]) => void,
    onMessagesCleared?: () => void
  ): (() => void) => {
    if (
      typeof window === "undefined" ||
      typeof window.addEventListener !== "function"
    ) {
      return () => undefined;
    }

    const sessionKey = MessageStorage.getSessionKey(userId);

    const handleStorageEvent = (event: StorageEvent) => {
      if (event.storageArea !== localStorage) {
        return;
      }

      const clearedAll = event.key === null && event.newValue === null;
      const isSessionKey = event.key === sessionKey;

      if (!isSessionKey && !clearedAll) {
        return;
      }

      if (event.newValue) {
        const updatedMessages = MessageStorage.loadMessages(userId);
        onMessagesUpdate(updatedMessages);
        return;
      }

      onMessagesCleared?.();
    };

    window.addEventListener("storage", handleStorageEvent);

    return () => {
      window.removeEventListener("storage", handleStorageEvent);
    };
  },
};

export default MessageStorage;
